/**
 * MENÜ KATALOĞU — tek kaynak.
 *
 * AppShell.tsx içinde module-local bir sabitti; buraya taşındı çünkü artık İKİ tüketicisi
 * var: sol menü ve SEKME ÇUBUĞU (sekme başlığı + ikonu buradan çözülür). İki ayrı yerde
 * tutulsaydı yeni bir sayfa eklendiğinde biri güncellenip diğeri unutulur, sekme
 * "Bilinmeyen sayfa" derdi.
 */

export interface NavItem {
  label: string;
  to: string;
  icon: string;
  /** Sayfa erişim anahtarı (Role.pages) — kullanıcının grubunda yoksa menüde gizlenir. */
  pageKey: string;
  phase?: string; // Henüz implement edilmemiş fazlar için badge
  disabled?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    label: "Operasyon",
    items: [
      { label: "Dashboard", to: "/dashboard", icon: "home", pageKey: "dashboard" },
      { label: "Stok Kartları", to: "/items/stock", icon: "package", pageKey: "items" },
      { label: "Yarı Mamuller", to: "/items/semi", icon: "boxes", pageKey: "items" },
      { label: "Mamuller", to: "/items/finished", icon: "cube", pageKey: "items" },
      { label: "Stok / Lotlar", to: "/inventory", icon: "boxes", pageKey: "inventory" },
      { label: "Siparişler", to: "/orders", icon: "list", pageKey: "orders" },
      { label: "Üretim", to: "/production", icon: "factory", pageKey: "production" },
      { label: "Tüketim Beyanı", to: "/tuketim-beyani", icon: "activity", pageKey: "tuketim-beyani" },
      { label: "Formlar", to: "/formlar", icon: "sheet", pageKey: "forms" },
      { label: "Kalite Kontrol", to: "/qc", icon: "checkCircle", pageKey: "qc" },
      { label: "Bakım / Onarım", to: "/maintenance", icon: "wrench", pageKey: "maintenance" },
      { label: "Projeler", to: "/projeler", icon: "list2", pageKey: "projects" },
      { label: "Personel / İK", to: "/personel", icon: "users", pageKey: "personel" },
      { label: "Depo Yönetimi", to: "/depo-yonetimi", icon: "warehouse", pageKey: "depo-yonetimi" },
      { label: "Sevkiyat", to: "/shipments", icon: "truck", pageKey: "shipments" },
      { label: "MRP / Planlama", to: "/mrp", icon: "chart", pageKey: "mrp" },
      { label: "Satınalma", to: "/purchasing", icon: "inbox", pageKey: "purchasing" },
    ],
  },
  {
    label: "Sistem",
    items: [
      { label: "Cariler", to: "/companies", icon: "building", pageKey: "companies" },
      { label: "Tedarikçiler", to: "/parties/suppliers", icon: "users", pageKey: "parties" },
      { label: "Müşteriler", to: "/parties/customers", icon: "users", pageKey: "parties" },
      { label: "Lokasyonlar", to: "/locations", icon: "mapPin", pageKey: "locations" },
      { label: "Reçeteler", to: "/recipes", icon: "list2", pageKey: "recipes" },
      { label: "Sertifikalar", to: "/certificates", icon: "checkCircle", pageKey: "certificates" },
      { label: "Kullanıcılar", to: "/users", icon: "users", pageKey: "users" },
      { label: "Ayarlar", to: "/settings", icon: "settings", pageKey: "settings" },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

/**
 * MOBİL ALT ÇUBUK — sahada en çok kullanılan 5 iş, tek dokunuşta.
 *
 * NEDEN AYRI LİSTE: mobil çekmece 25 kalem taşıyor; operatör vardiya boyunca bunların
 * yalnız birkaçına giriyor ve her seferinde hamburger → kaydır → dokun yapıyordu.
 * Alt çubuk bu beşini sabitler; çekmece tam menü olarak KALIR (kaldırılmadı).
 *
 * SIRA SABİT ve `NAV`tan TÜRETİLİR: yol/ikon/pageKey tek yerde tanımlı kalsın diye
 * burada yalnız `to` ile referans verilir. Menüde bir yol değişirse burası kendiliğinden
 * uyar; kopyalanmış bir yol sessizce eskimez.
 *
 * ORTA KALEM (`Üretim`) büyük yuvarlak olarak çizilir — sahada en sık dokunulan iş odur.
 */
const TABBAR_PATHS = ["/inventory", "/orders", "/production", "/shipments", "/purchasing"] as const;

export interface TabBarItem extends NavItem {
  /** Alt çubukta kısaltılmış etiket (5 kalem 375px'e sığmalı). */
  short: string;
  /** Ortadaki büyük yuvarlak kalem mi. */
  center: boolean;
}
const TABBAR_SHORT: Record<string, string> = {
  "/inventory": "Stok/Lot",
  "/orders": "Sipariş",
  "/production": "Üretim",
  "/shipments": "Sevkiyat",
  "/purchasing": "Satınalma",
};
export const MOBILE_TABBAR: TabBarItem[] = TABBAR_PATHS.map((p, i) => {
  const item = NAV_ITEMS.find((n) => n.to === p);
  // Katalogda olmayan yol = geliştirici hatası; sessizce boş çubuk çizmek yerine patlat.
  if (!item) throw new Error(`MOBILE_TABBAR: "${p}" NAV kataloğunda yok`);
  return { ...item, short: TABBAR_SHORT[p] ?? item.label, center: i === 2 };
});

/**
 * Menüde GÖRÜNMEYEN ama sekme olarak açılabilen yollar.
 * Menüden erişilmiyorlar (derin bağlantı, alt sayfa) ama sekme çubuğu bunlara da
 * bir ad vermek zorunda — aksi hâlde başlıksız sekme çıkardı.
 */
const EXTRA: NavItem[] = [
  { label: "Yeni Stok Kartı", to: "/items/stock/yeni", icon: "plus", pageKey: "items" },
  { label: "Yeni Yarı Mamul", to: "/items/semi/yeni", icon: "plus", pageKey: "items" },
  { label: "Yeni Mamul", to: "/items/finished/yeni", icon: "plus", pageKey: "items" },
  { label: "Yeni Sipariş", to: "/orders/yeni", icon: "plus", pageKey: "orders" },
  { label: "Toplu Fazla Mesai", to: "/personel/mesai", icon: "clock", pageKey: "personel" },
  { label: "Toplu Devamsızlık", to: "/personel/devamsizlik", icon: "userX", pageKey: "personel" },
  { label: "Üretim Parametre Raporu", to: "/production/parametre-raporu", icon: "chart", pageKey: "production" },
  { label: "Form Panosu", to: "/formlar/panel", icon: "chart", pageKey: "forms" },
  { label: "Form Listesi", to: "/formlar/liste", icon: "sheet", pageKey: "forms" },
];

const BY_PATH = new Map<string, NavItem>([...NAV_ITEMS, ...EXTRA].map((i) => [i.to, i]));

/**
 * SEKME AÇILMAYAN yollar — bunlar bir sayfa değil, YÖNLENDİRMEDİR.
 * Örnek: `/formlar` anında `/formlar/liste`ye atar. Sekme açılsaydı biri ölü
 * (yönlendirme) diğeri gerçek olmak üzere İKİ sekme kalırdı.
 */
export const TAB_SKIP_PATHS = new Set(["/", "/items", "/parties", "/formlar"]);

export interface TabMeta {
  title: string;
  icon: string;
  pageKey: string;
}

/**
 * Yol → sekme künyesi.
 *
 * ÖNCE TAM EŞLEŞME: `/items/stock` ile `/items/semi` AYNI bileşeni render eder ama
 * FARKLI sekmelerdir ve farklı adları vardır ("Stok Kartları" / "Yarı Mamuller");
 * önek eşleşmesi ikisini de "Stok Kartları" yapardı.
 * Sonra en UZUN önek: `/production/parametre-raporu` gibi alt yollar.
 */
export function tabMetaForPath(pathname: string): TabMeta {
  const exact = BY_PATH.get(pathname);
  if (exact) return { title: exact.label, icon: exact.icon, pageKey: exact.pageKey };

  /**
   * Cari hesap ekstresi — `/companies/<id>/hesap`.
   * Önek kuralı bunu "Cariler" adlandırırdı ve kullanıcı liste sekmesi ile ekstre
   * sekmesini ayırt edemezdi. Kendi adı + kendi ikonu olsun; `pageKey` "companies"
   * kalır çünkü sayfa erişimi cari yetkisiyle aynı kapıdan geçer.
   */
  if (/^\/companies\/[^/]+\/hesap$/.test(pathname)) {
    return { title: "Cari Hesap", icon: "banknote", pageKey: "companies" };
  }

  let best: NavItem | null = null;
  for (const i of BY_PATH.values()) {
    if (pathname === i.to || pathname.startsWith(i.to + "/")) {
      if (!best || i.to.length > best.to.length) best = i;
    }
  }
  if (best) return { title: best.label, icon: best.icon, pageKey: best.pageKey };
  // Katalogda olmayan yol — sekme yine de adlandırılır (boş sekme başlığı olmaz).
  return { title: pathname.replace(/^\//, "") || "Sayfa", icon: "file", pageKey: "" };
}
